/** An invoice record associated with an order. */
export interface Invoice {
  /** Invoice ID. */
  id?: string;
  /** ID of the app that set the invoice. */
  appId?: string;
}

export interface ListInvoicesForSingleOrderRequest {
  /**
   * Order ID.
   * @minLength 1
   * @maxLength 100
   */
  orderId?: string;
}

export interface ListInvoicesForSingleOrderResponse {
  /** List of invoices. */
  invoices?: Invoice[];
}

export interface ListInvoicesForMultipleOrdersRequest {
  /**
   * Order IDs for which to retrieve invoices.
   * @maxSize 100
   * @minLength 1
   * @maxLength 100
   */
  orderIds: string[];
}

export interface ListInvoicesForMultipleOrdersResponse {
  /** List of order IDs and their associated invoices. */
  invoicesForOrder?: InvoicesForOrder[];
}

export interface InvoicesForOrder {
  /** Order ID. */
  orderId?: string;
  /**
   * Invoices info.
   * @maxSize 300
   */
  invoicesInfo?: Invoice[];
}

export interface GenerateInvoiceRequest {
  /**
   * Order ID.
   * @minLength 1
   * @maxLength 100
   */
  orderId: string;
}

export interface GenerateInvoiceResponse {
  /** Invoice ID. */
  invoiceId?: string;
}

export interface BulkGenerateInvoicesRequest {
  /**
   * Order IDs.
   * @minSize 1
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  orderIds: string[];
}

export interface BulkGenerateInvoicesResponse {
  results?: BulkInvoiceResult[];
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkInvoiceResult {
  itemMetadata?: ItemMetadata;
  item?: InvoiceForOrder;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface InvoiceForOrder {
  /** Order ID. */
  orderId?: string;
  /** Invoice ID. */
  invoiceId?: string;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface AddInvoiceToOrderRequest {
  /**
   * Order ID.
   * @minLength 1
   * @maxLength 100
   */
  orderId: string;
  /** Invoice info. */
  invoiceInfo: Invoice;
}

export interface AddInvoiceToOrderResponse {
  /** List of order invoices. */
  orderInvoices?: Invoice[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface GetOrderInvoiceRequest {
  /**
   * Invoice ID.
   * @minLength 1
   * @maxLength 100
   */
  invoiceId?: string;
}

export interface GetOrderInvoiceResponse {
  /**
   * Order ID.
   * @minLength 1
   * @maxLength 100
   */
  orderId?: string;
  /** Invoice info. */
  invoiceInfo?: Invoice;
}

export interface GenerateInvoiceWithNumberRequest {
  /**
   * Order ID.
   * @minLength 1
   * @maxLength 100
   */
  orderId?: string;
  /**
   * @minLength 1
   * @maxLength 11
   */
  invoiceNumber?: string;
  /** Date and time the payment was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided. */
  issueDate?: Date | null;
}

export interface GenerateInvoiceWithNumberResponse {
  /**
   * Invoice ID.
   * @minLength 1
   * @maxLength 100
   */
  invoiceId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type GenerateInvoiceApplicationErrors =
  | {
      code?: 'GENERATE_INVOICE_NOT_SUPPORTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVOICE_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MISSING_CONTACT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MISSING_CONTACT_EMAIL';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_EMAIL';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PRICE_AMOUNT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CONTACT_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FAILED_TO_RETRIEVE_CONTACT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNKNOWN_CURRENCY';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_HAS_CHARGEBACKS';
      description?: string;
      data?: Record<string, any>;
    };
